// frontend/src/components/CreditPurchaseModal.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import creditService from '../services/credit/creditService';
import './CreditPurchaseModal.css';

const CreditPurchaseModal = ({ isOpen, onClose }) => {
  const { updateCreditBalance } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('basic');
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const modalContentRef = useRef(null);
  
  // Credit packages
  const PACKAGES = {
    basic: {
      id: 'basic',
      name: 'Basic Package',
      price: 10,
      credits: 100000,
      description: 'Approximately 25,000 words of translation'
    },
    standard: {
      id: 'standard',
      name: 'Standard Package',
      price: 25,
      credits: 300000,
      description: 'Approximately 75,000 words of translation'
    },
    professional: {
      id: 'professional',
      name: 'Professional Package',
      price: 50,
      credits: 700000,
      description: 'Approximately 175,000 words of translation'
    }
  };
  
  // Handle click outside the modal to close it
  const handleClickOutside = (e) => {
    // If loading, don't allow closing
    if (loading) return;
    
    // If click is outside modal content, close the modal
    if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
      onClose();
    }
  };
  
  // Set up click outside listener when modal is open
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, loading, onClose]);
  
  // Get Stripe publishable key from backend
  useEffect(() => {
    if (isOpen && !stripePublishableKey) {
      const getConfig = async () => {
        try {
          const config = await creditService.getStripeConfig();
          if (config.error) {
            setError(config.message || "Failed to load payment system");
            return;
          }
          
          setStripePublishableKey(config.publishable_key);
        } catch (err) {
          console.error("Failed to get Stripe configuration:", err);
          setError("Failed to load payment system");
        }
      };
      
      getConfig();
    }
  }, [isOpen, stripePublishableKey]);
  
  // Load Stripe.js once
  useEffect(() => {
    if (!stripePublishableKey || stripe) return;
    
    const loadStripe = async () => {
      if (window.Stripe) {
        setStripe(window.Stripe(stripePublishableKey));
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      
      script.onload = () => {
        setStripe(window.Stripe(stripePublishableKey));
      };
      
      document.body.appendChild(script);
    };
    
    loadStripe();
  }, [stripePublishableKey, stripe]);
  
  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset form state but don't touch stripe or card element
      setLoading(false);
      setError('');
      setSuccess('');
      setCardComplete(false);
    }
  }, [isOpen]);
  
  // Handle creating the card element
  useEffect(() => {
    // If modal is closed, or stripe isn't loaded, do nothing
    if (!isOpen || !stripe) return;
    
    // If card element doesn't exist or needs recreation
    if (!cardElement) {
      try {
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#32325d',
              fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a',
            },
          },
          // Enable Link functionality
          wallets: {
            applePay: 'auto',
            googlePay: 'auto'
          }
        });
        
        setCardElement(card);
        
        // Mount in a timeout to ensure DOM is ready
        setTimeout(() => {
          const cardContainer = document.getElementById('card-element');
          if (cardContainer) {
            card.mount('#card-element');
            
            card.on('change', (event) => {
              setCardComplete(event.complete);
              if (event.error) {
                setError(event.error.message);
              } else {
                setError('');
              }
            });
          }
        }, 100);
      } catch (err) {
        console.error('Error creating card element:', err);
        setError('Failed to initialize payment form. Please try again.');
      }
    } else {
      // If card element exists but needs remounting
      const cardContainer = document.getElementById('card-element');
      if (cardContainer && cardElement) {
        try {
          cardElement.mount('#card-element');
        } catch (err) {
          console.error('Error remounting card element:', err);
        }
      }
    }
    
    return () => {
      // On effect cleanup, unmount but don't destroy
      if (cardElement && isOpen) {
        try {
          cardElement.unmount();
        } catch (e) {
          // Ignore unmounting errors
        }
      }
    };
  }, [isOpen, stripe, cardElement]);
  
  // Reset after payment
  const resetAfterPayment = () => {
    // Clear the card input but maintain the Link/saved card functionality
    if (cardElement) {
      try {
        cardElement.clear();
      } catch (e) {
        console.log('Error clearing card element:', e);
      }
    }
    
    // Reset other form state but keep the success/error messages
    setCardComplete(false);
    setLoading(false);
    setSelectedPackage('basic');
  };
  
  // Auto-dismiss success message after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 8000); // Keep success message visible for 8 seconds
      
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !cardElement) {
      setError('Payment system not fully loaded. Please try again.');
      return;
    }
    
    // Clear any existing messages before starting a new payment
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // First create a payment method using the card element
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement
      });
      
      if (methodError) {
        throw new Error(methodError.message);
      }
      
      // Create payment intent through our credit service
      const paymentIntent = await creditService.createPaymentIntent(selectedPackage);
      
      if (paymentIntent.error) {
        throw new Error(paymentIntent.message || "Failed to create payment");
      }
      
      // Confirm payment using the payment method ID
      const { error: confirmError, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
        paymentIntent.client_secret, 
        { payment_method: paymentMethod.id }
      );
      
      if (confirmError) {
        throw new Error(confirmError.message);
      }
      
      if (confirmedIntent.status !== 'succeeded') {
        throw new Error('Payment failed. Please try again.');
      }
      
      // Add credits through our credit service
      const result = await creditService.addCredits(confirmedIntent.id);
      
      if (result.error) {
        throw new Error(result.message || "Failed to add credits");
      }
      
      // Update credit balance in context
      await updateCreditBalance();
      
      // Show success message
      setSuccess(`Successfully purchased ${PACKAGES[selectedPackage].name}!`);
      
      // Reset form for another purchase
      resetAfterPayment();
      
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "Payment failed. Please try again.");
      setLoading(false);
    }
  };
  
  // Function to dismiss success/error messages
  const dismissMessage = () => {
    setSuccess('');
    setError('');
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-backdrop">
      <div 
        ref={modalContentRef} 
        className="modal-content credit-purchase-modal"
      >
        <h2 className="text-xl font-semibold mb-4">Purchase Translation Credits</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex justify-between items-start">
            <div>{error}</div>
            <button 
              onClick={dismissMessage} 
              className="ml-2 text-red-700 hover:text-red-900"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded flex justify-between items-start">
            <div>{success}</div>
            <button 
              onClick={dismissMessage} 
              className="ml-2 text-green-700 hover:text-green-900"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
        
        <div className="package-selector mb-4">
          <p className="text-sm text-gray-600 mb-2">Select a credit package:</p>
          
          <div className="packages-grid">
            {Object.values(PACKAGES).map(pkg => (
              <div 
                key={pkg.id}
                className={`package-card ${selectedPackage === pkg.id ? 'selected' : ''}`}
                onClick={() => !loading && setSelectedPackage(pkg.id)}
              >
                <div className="package-name">{pkg.name}</div>
                <div className="package-price">${pkg.price}</div>
                <div className="package-credits">{pkg.credits.toLocaleString()} credits</div>
                <div className="package-description text-xs text-gray-500">{pkg.description}</div>
              </div>
            ))}
          </div>
        </div>
        
        <form onSubmit={handlePayment}>
          <div className="mb-4">
            <label className="form-label">Card Information</label>
            <div id="card-element" className="card-element"></div>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !stripe || !cardComplete}
            >
              {loading ? 'Processing...' : `Purchase for $${PACKAGES[selectedPackage].price}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;